import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, CheckCircle2 } from "lucide-react";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { useLang } from "@/context/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;

const COUNTRY_CODES = ["IT", "CH", "SM", "FR", "DE", "ES", "AT", "SI"];

/**
 * Shared registration form used on /registrati (via role toggle) and at checkout when the
 * guest wants to also create an account.
 *
 * Fields:
 *   Cliente     → full_name, phone, email, address_line, city, country, tax_code (opt), password
 *   Fornitore   → company + full_name, phone, email, address_line, city, country, tax_code (req), password
 */
export default function RegistrationForm({ role = "customer", onSuccess, embedded = false }) {
  const { register } = useCustomerAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [form, setForm] = useState({
    company: "",
    full_name: "",
    email: "",
    phone: "",
    address_line: "",
    civic_number: "",
    city: "",
    country: "IT",
    postal_code: "",
    tax_code: "",
    password: "",
    confirm: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [supplierPending, setSupplierPending] = useState(false);
  const isSupplier = role === "supplier";
  const isCustomer = role === "customer";

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (form.password.length < 8) return t("reg_err_password_short");
    if (form.password !== form.confirm) return t("reg_err_password_mismatch");
    if (isSupplier && !form.company.trim()) return t("reg_err_company");
    if (isSupplier && !form.tax_code.trim()) return t("reg_err_taxcode");
    if (
      !form.full_name.trim() || !form.phone.trim() || !form.email.trim() ||
      !form.address_line.trim() || !form.civic_number.trim() ||
      !form.city.trim() || !form.postal_code.trim() || !form.country.trim()
    ) {
      return t("reg_err_required");
    }
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return toast.error(err);
    setLoading(true);

    if (isSupplier) {
      try {
        const res = await fetch(`${API}/api/supplier/register`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: form.email.trim(),
            password: form.password,
            full_name: form.full_name.trim(),
            phone: form.phone.trim(),
            company: form.company.trim(),
            tax_code: form.tax_code.trim(),
            address_line: form.address_line.trim(),
            civic_number: form.civic_number.trim(),
            city: form.city.trim(),
            country: form.country,
            postal_code: form.postal_code.trim(),
            notes: form.notes.trim(),
          }),
        });
        const data = await res.json();
        if (!res.ok) {
          const detail = data?.detail;
          throw new Error(typeof detail === "string" ? detail : t("reg_err_generic"));
        }
        setSupplierPending(true);
        toast.success(t("reg_supplier_sent"));
        onSuccess?.({ role: "supplier", pending: true });
      } catch (er) {
        toast.error(er.message);
      } finally { setLoading(false); }
      return;
    }

    // Customer flow → uses CustomerAuthContext to auto-login
    try {
      await register({
        email: form.email.trim(),
        password: form.password,
        full_name: form.full_name.trim(),
        phone: form.phone.trim(),
        address_line: form.address_line.trim(),
        civic_number: form.civic_number.trim(),
        city: form.city.trim(),
        country: form.country,
        postal_code: form.postal_code.trim(),
        tax_code: form.tax_code.trim(),
      });
      toast.success(t("reg_customer_success"));
      if (onSuccess) onSuccess({ role: "customer" });
      else nav("/account");
    } catch (er) {
      const detail = er.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("reg_err_generic"));
    } finally { setLoading(false); }
  };

  if (supplierPending) {
    return (
      <div className="bg-white border border-emerald-200 rounded-xl p-6 mt-6 text-center" data-testid="supplier-pending-success">
        <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
        <div className="font-display text-xl font-semibold mt-3">{t("reg_pending_title")}</div>
        <p className="text-sm text-xd-muted mt-2 max-w-md mx-auto">
          {t("reg_pending_desc")}
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className={`bg-white border border-xd-border rounded-xl p-6 mt-6 space-y-3 ${embedded ? "shadow-none" : ""}`}
      data-testid={`register-form-${role}`}
      noValidate
    >
      {isSupplier && (
        <Field label={t("reg_company")} testid="register-company" value={form.company} onChange={(v) => update("company", v)} required />
      )}

      <Field label={isSupplier ? t("reg_fullname_supplier") : t("reg_fullname_customer")} testid="register-fullname" value={form.full_name} onChange={(v) => update("full_name", v)} required />

      <div className="grid sm:grid-cols-2 gap-3">
        <Field type="email" label={t("reg_email")} testid="register-email" value={form.email} onChange={(v) => update("email", v)} required />
        <Field type="tel" label={t("reg_phone")} testid="register-phone" value={form.phone} onChange={(v) => update("phone", v)} required />
      </div>

      <div className="grid sm:grid-cols-[1fr_140px] gap-3">
        <Field label={t("reg_address")} testid="register-address" value={form.address_line} onChange={(v) => update("address_line", v)} required />
        <Field label={t("reg_civic")} testid="register-civic" value={form.civic_number} onChange={(v) => update("civic_number", v)} required />
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <Field label={t("reg_postal")} testid="register-postal" value={form.postal_code} onChange={(v) => update("postal_code", v)} required />
        <Field label={t("reg_city")} testid="register-city" value={form.city} onChange={(v) => update("city", v)} required />
        <div>
          <label className="text-xs font-medium text-xd-muted uppercase tracking-widest">{t("reg_country")}</label>
          <select
            required
            value={form.country}
            onChange={(e) => update("country", e.target.value)}
            data-testid="register-country"
            className="mt-1 w-full h-11 px-3 rounded-lg border border-xd-border text-sm focus:outline-none focus:border-xd-cyan bg-white"
          >
            {COUNTRY_CODES.map((code) => (
              <option key={code} value={code}>{t(`country_${code}`)}</option>
            ))}
          </select>
        </div>
      </div>

      <Field
        label={isSupplier ? t("reg_taxcode_required") : t("reg_taxcode_optional")}
        testid="register-taxcode"
        value={form.tax_code}
        onChange={(v) => update("tax_code", v)}
        required={isSupplier}
      />

      {isSupplier && (
        <div>
          <label className="text-xs font-medium text-xd-muted uppercase tracking-widest">{t("reg_notes_label")}</label>
          <textarea
            rows={2}
            value={form.notes}
            onChange={(e) => update("notes", e.target.value)}
            placeholder={t("reg_notes_placeholder")}
            data-testid="register-notes"
            className="mt-1 w-full px-3 py-2 rounded-lg border border-xd-border text-sm focus:outline-none focus:border-xd-cyan"
          />
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3 pt-2 border-t border-xd-border">
        <Field type="password" label={t("reg_password")} testid="register-password" value={form.password} onChange={(v) => update("password", v)} required minLength={8} />
        <Field type="password" label={t("reg_confirm")} testid="register-confirm" value={form.confirm} onChange={(v) => update("confirm", v)} required minLength={8} />
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid={`register-submit-${role}`}
        className="w-full btn-cta rounded-lg py-3 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
      >
        <UserPlus className="w-4 h-4" />
        {loading
          ? t("reg_loading")
          : isCustomer ? t("reg_cta_customer") : t("reg_cta_supplier")}
      </button>

      {isSupplier && (
        <p className="text-[11px] text-xd-muted text-center pt-1">
          {t("reg_supplier_footnote")}
        </p>
      )}
    </form>
  );
}

function Field({ label, value, onChange, testid, type = "text", required = false, minLength }) {
  return (
    <div>
      <label className="text-xs font-medium text-xd-muted uppercase tracking-widest">{label}</label>
      <input
        type={type}
        required={required}
        minLength={minLength}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1 w-full h-11 px-3 rounded-lg border border-xd-border text-sm focus:outline-none focus:border-xd-cyan"
      />
    </div>
  );
}
